import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Building2,
  MapPin,
  Briefcase,
  Building,
  CheckCircle,
  Settings,
  Search,
  Stethoscope,
  Shield,
  CreditCard,
  Activity,
  DollarSign,
  Navigation
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CurrencyInput } from './ui/currency-input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SALARY_FIELDS = [
  { key: 'salario_junior_i', label: 'Júnior I' },
  { key: 'salario_junior_ii', label: 'Júnior II' },
  { key: 'salario_junior_iii', label: 'Júnior III' },
  { key: 'salario_pleno_i', label: 'Pleno I' },
  { key: 'salario_pleno_ii', label: 'Pleno II' },
  { key: 'salario_pleno_iii', label: 'Pleno III' },
  { key: 'salario_senior_i', label: 'Sênior I' },
  { key: 'salario_senior_ii', label: 'Sênior II' },
  { key: 'salario_senior_iii', label: 'Sênior III' },
];

const getDefaultCargoData = () => ({
  empresa: '',
  cargo: '',
  departamento: '',
  modalidade: 'CLT',
  ...SALARY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {})
});

const ConfigPage = ({ defaultTab = 'companies' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  const tabRoutes = {
    companies: '/cadastros/empresas',
    branches: '/cadastros/filiais',
    positions: '/cadastros/cargos',
    departments: '/cadastros/departamentos',
    statuses: '/cadastros/situacoes',
    odonto: '/cadastros/odonto',
    segVida: '/cadastros/seg-vida',
    totalPass: '/cadastros/total-pass',
    medOcupacional: '/cadastros/med-ocupacional',
    companyAddresses: '/cadastros/enderecos',
  };
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [positions, setPositions] = useState([]);
  const [positionSalaries, setPositionSalaries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [odontoOptions, setOdontoOptions] = useState([]);
  const [segVidaOptions, setSegVidaOptions] = useState([]);
  const [totalPassOptions, setTotalPassOptions] = useState([]);
  const [medOcupacionalOptions, setMedOcupacionalOptions] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  
  // Dialog states
  const [dialog, setDialog] = useState({ 
    open: false, 
    type: '', 
    action: 'create',
    data: { 
      name: '', 
      company_id: '', 
      value: '',
      // Company address fields
      nome_empresa: '',
      endereco_completo: '',
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      is_default: false
    }
  });
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ 
    open: false, 
    type: '', 
    item: null 
  });

  // Cargo + Salário dialog (Cadastros > Cargos)
  const [cargoDialog, setCargoDialog] = useState({
    open: false,
    action: 'create',
    data: getDefaultCargoData()
  });

  useEffect(() => {
    setActiveTab(defaultTab);
    fetchAllData();
  }, [defaultTab, location.key]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [
        companiesRes, 
        branchesRes, 
        positionsRes, 
        departmentsRes, 
        statusRes,
        odontoRes,
        segVidaRes,
        totalPassRes,
        medOcupacionalRes,
        companyAddressesRes,
        positionSalariesRes
      ] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/branches`),
        axios.get(`${API}/positions`),
        axios.get(`${API}/departments`),
        axios.get(`${API}/status`),
        axios.get(`${API}/odonto-options`),
        axios.get(`${API}/seg-vida-options`),
        axios.get(`${API}/total-pass-options`),
        axios.get(`${API}/med-ocupacional-options`),
        axios.get(`${API}/company-addresses`),
        axios.get(`${API}/position-salaries`).catch(() => ({ data: [] }))
      ]);

      setCompanies(companiesRes.data);
      setBranches(branchesRes.data);
      setPositions(positionsRes.data);
      setPositionSalaries(positionSalariesRes.data);
      setDepartments(departmentsRes.data);
      setStatuses(statusRes.data);
      setOdontoOptions(odontoRes.data);
      setSegVidaOptions(segVidaRes.data);
      setTotalPassOptions(totalPassRes.data);
      setMedOcupacionalOptions(medOcupacionalRes.data);
      setCompanyAddresses(companyAddressesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const endpoints = {
        companies: '/companies',
        branches: '/branches',
        positions: '/positions',
        departments: '/departments',
        statuses: '/status',
        odonto: '/odonto-options',
        segVida: '/seg-vida-options',
        totalPass: '/total-pass-options',
        medOcupacional: '/med-ocupacional-options',
        companyAddresses: '/company-addresses'
      };

      // Different payload based on type
      let payload;
      if (dialog.type === 'branches') {
        payload = { name: dialog.data.name, company_id: dialog.data.company_id };
      } else if (['odonto', 'segVida', 'totalPass', 'medOcupacional'].includes(dialog.type)) {
        payload = { name: dialog.data.name, value: dialog.data.value ?? 0 };
      } else if (dialog.type === 'companyAddresses') {
        payload = {
          nome_empresa: dialog.data.nome_empresa,
          endereco_completo: dialog.data.endereco_completo,
          cep: dialog.data.cep,
          logradouro: dialog.data.logradouro,
          numero: dialog.data.numero,
          complemento: dialog.data.complemento,
          bairro: dialog.data.bairro,
          cidade: dialog.data.cidade,
          estado: dialog.data.estado,
          is_default: dialog.data.is_default
        };
      } else {
        payload = { name: dialog.data.name };
      }

      if (dialog.action === 'edit') {
        // Update existing item
        await axios.put(`${API}${endpoints[dialog.type]}/${dialog.data.id}`, payload);
        toast.success('Item atualizado com sucesso!');
      } else {
        // Create new item
        await axios.post(`${API}${endpoints[dialog.type]}`, payload);
        toast.success('Item criado com sucesso!');
      }
      
      fetchAllData();
      setDialog({ open: false, type: '', action: 'create', data: getDefaultDialogData() });
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      toast.error(`Erro ao ${dialog.action === 'edit' ? 'atualizar' : 'criar'} item`);
    }
  };

  const handleDelete = async (type, item) => {
    try {
      const endpoints = {
        companies: `/companies/${item.id}`,
        branches: `/branches/${item.id}`,
        positions: `/position-salaries/${item.id}`,
        departments: `/departments/${item.id}`,
        statuses: `/status/${item.id}`,
        odonto: `/odonto-options/${item.id}`,
        segVida: `/seg-vida-options/${item.id}`,
        totalPass: `/total-pass-options/${item.id}`,
        medOcupacional: `/med-ocupacional-options/${item.id}`,
        companyAddresses: `/company-addresses/${item.id}`
      };

      await axios.delete(`${API}${endpoints[type]}`);
      toast.success('Item removido com sucesso!');
      fetchAllData();
      setDeleteDialog({ open: false, type: '', item: null });
    } catch (error) {
      console.error('Erro ao remover item:', error);
      toast.error('Erro ao remover item');
    }
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      setDialog(prev => ({
        ...prev,
        data: {
          ...prev.data,
          logradouro: response.data.logradouro || '',
          bairro: response.data.bairro || '',
          cidade: response.data.localidade || '',
          estado: response.data.uf || '',
          complemento: response.data.complemento || prev.data.complemento
        }
      }));

      toast.success('Endereço preenchido automaticamente');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    }
  };

  const getDefaultDialogData = (type = '') => {
    if (type === 'companyAddresses') {
      return {
        nome_empresa: '',
        endereco_completo: '',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        is_default: false
      };
    }
    return { name: '', company_id: '', value: '' };
  };

  // Busca endereço automaticamente via API ViaCEP a partir do CEP digitado
  const handleCepLookup = async (cepValue) => {
    const cleanCep = (cepValue || '').replace(/\D/g, '');
    if (cleanCep.length !== 8) return;
    try {
      // Usa fetch (sem o header Authorization global do axios) para não vazar o JWT à API externa
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }
      const enderecoCompleto = [data.logradouro, data.bairro, data.localidade, data.uf]
        .filter(Boolean)
        .join(', ');
      setDialog(prev => ({
        ...prev,
        data: {
          ...prev.data,
          logradouro: data.logradouro || prev.data.logradouro,
          bairro: data.bairro || prev.data.bairro,
          cidade: data.localidade || prev.data.cidade,
          estado: data.uf || prev.data.estado,
          endereco_completo: enderecoCompleto || prev.data.endereco_completo
        }
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    }
  };

  const openDialog = (type, action = 'create', item = null) => {
    const defaultData = getDefaultDialogData(type);
    
    setDialog({
      open: true,
      type,
      action,
      data: item ? { ...item } : defaultData
    });
  };

  // Abre o modal de Cargo + Salário sem sair da página (Cadastros > Cargos)
  const openCargoDialog = (action = 'create', item = null) => {
    if (action === 'edit' && item) {
      setCargoDialog({
        open: true,
        action: 'edit',
        data: {
          id: item.id,
          empresa: item.empresa || '',
          cargo: item.cargo || '',
          departamento: item.departamento || '',
          modalidade: item.modalidade || 'CLT',
          ...SALARY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: item[f.key] ?? '' }), {})
        }
      });
    } else {
      setCargoDialog({ open: true, action: 'create', data: getDefaultCargoData() });
    }
  };

  // Envio assíncrono do formulário de Cargo + Salário; fecha o modal e atualiza a tabela no sucesso
  const handleSaveCargo = async () => {
    if (!cargoDialog.data.empresa || !cargoDialog.data.cargo) {
      toast.error('Preencha empresa e cargo');
      return;
    }
    try {
      const payload = {
        empresa: cargoDialog.data.empresa,
        cargo: cargoDialog.data.cargo,
        departamento: cargoDialog.data.departamento,
        modalidade: cargoDialog.data.modalidade,
        ...SALARY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: cargoDialog.data[f.key] ?? 0 }), {})
      };
      if (cargoDialog.action === 'edit') {
        await axios.put(`${API}/position-salaries/${cargoDialog.data.id}`, payload);
        toast.success('Cargo atualizado com sucesso!');
      } else {
        await axios.post(`${API}/position-salaries`, payload);
        toast.success('Cargo criado com sucesso!');
      }
      await fetchAllData();
      setCargoDialog({ open: false, action: 'create', data: getDefaultCargoData() });
    } catch (error) {
      console.error('Erro ao salvar cargo:', error);
      toast.error('Erro ao salvar cargo e salário');
    }
  };

  const tabs = [
    { key: 'companies', label: 'Empresas', icon: Building2, data: companies },
    { key: 'branches', label: 'Filiais', icon: MapPin, data: branches },
    { key: 'positions', label: 'Cargos', icon: Briefcase, data: positionSalaries },
    { key: 'departments', label: 'Departamentos', icon: Building, data: departments },
    { key: 'statuses', label: 'Situações', icon: CheckCircle, data: statuses },
    { key: 'odonto', label: 'Odonto', icon: Stethoscope, data: odontoOptions },
    { key: 'segVida', label: 'Seg. Vida', icon: Shield, data: segVidaOptions },
    { key: 'totalPass', label: 'Total Pass', icon: CreditCard, data: totalPassOptions },
    { key: 'medOcupacional', label: 'Med. Ocupacional', icon: Activity, data: medOcupacionalOptions },
    { key: 'companyAddresses', label: 'Endereços', icon: Navigation, data: companyAddresses },
  ];

  const getTabTitle = (type) => {
    const titles = {
      companies: 'Empresas',
      branches: 'Filiais',
      positions: 'Cargos',
      departments: 'Departamentos',
      statuses: 'Situações',
      odonto: 'Opções de Odonto',
      segVida: 'Opções de Seguro de Vida',
      totalPass: 'Opções de Total Pass',
      medOcupacional: 'Opções de Med. Ocupacional',
      companyAddresses: 'Endereços da Empresa'
    };
    return titles[type] || '';
  };

  // Helper function to check if it's a selection option type
  const isSelectionOption = (type) => {
    return ['odonto', 'segVida', 'totalPass', 'medOcupacional'].includes(type);
  };

  // Format currency for selection options
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Filter function
  const getFilteredData = (data) => {
    if (!searchTerm) return data;
    
    return data.filter(item => {
      if (!item) return false;
      
      // For company addresses, search in different fields
      if (activeTab === 'companyAddresses') {
        const nomeEmpresa = item.nome_empresa || '';
        const endereco = item.endereco_completo || '';
        const cidade = item.cidade || '';
        const estado = item.estado || '';
        
        return nomeEmpresa.toLowerCase().includes(searchTerm.toLowerCase()) ||
               endereco.toLowerCase().includes(searchTerm.toLowerCase()) ||
               cidade.toLowerCase().includes(searchTerm.toLowerCase()) ||
               estado.toLowerCase().includes(searchTerm.toLowerCase());
      }
      
      // For positions (cargos), search across cargo/empresa/departamento
      if (activeTab === 'positions') {
        const term = searchTerm.toLowerCase();
        return (item.cargo || '').toLowerCase().includes(term) ||
               (item.empresa || '').toLowerCase().includes(term) ||
               (item.departamento || '').toLowerCase().includes(term);
      }

      // For other types, use the name field
      const itemName = item.name || '';
      let matches = itemName.toLowerCase().includes(searchTerm.toLowerCase());
      
      // For branches, also search in company name
      if (activeTab === 'branches') {
        const company = companies.find(c => c.id === item.company_id);
        const companyName = company?.name || '';
        matches = matches || companyName.toLowerCase().includes(searchTerm.toLowerCase());
      }
      
      return matches;
    });
  };

  // Navigate to route when tab changes
  const handleTabChange = (tabKey) => {
    const route = tabRoutes[tabKey];
    if (route) navigate(route);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Cadastros</h1>
          <p className="text-slate-600 mt-1">
            Gerencie empresas, filiais, cargos, departamentos, situações e opções de seleção
          </p>
        </div>
      </div>

      {/* Tab navigation */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              
              return (
                <Button
                  key={tab.key}
                  variant={isActive ? "default" : "outline"}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex items-center space-x-2 ${
                    isActive ? 'btn-primary' : ''
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  <Badge variant="secondary" className="ml-2">
                    {tab.data.length}
                  </Badge>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Content area */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              {React.createElement(tabs.find(t => t.key === activeTab)?.icon, { className: "h-5 w-5" })}
              <span>Gerenciar {getTabTitle(activeTab)}</span>
            </CardTitle>
            <CardDescription>
              Adicione, edite ou remova {getTabTitle(activeTab).toLowerCase()} do sistema
            </CardDescription>
          </div>
          <Button
            onClick={() => activeTab === 'positions' ? openCargoDialog('create') : openDialog(activeTab)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Adicionar</span>
          </Button>
        </CardHeader>
        <CardContent>
          {/* Search Field */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder={`Buscar ${getTabTitle(activeTab).toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {activeTab === 'companyAddresses' ? (
                    <>
                      <TableHead>Nome da Empresa</TableHead>
                      <TableHead>Endereço</TableHead>
                      <TableHead>Cidade</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-center">Padrão</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </>
                  ) : activeTab === 'positions' ? (
                    <>
                      <TableHead>Cargo</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Departamento</TableHead>
                      <TableHead>Modalidade</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead>Nome</TableHead>
                      {activeTab === 'branches' && <TableHead>Empresa</TableHead>}
                      {isSelectionOption(activeTab) && <TableHead className="text-right">Valor</TableHead>}
                      <TableHead className="text-center">Ações</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const currentData = tabs.find(t => t.key === activeTab)?.data || [];
                  const filteredData = getFilteredData(currentData);
                  
                  if (filteredData.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={
                          activeTab === 'companyAddresses' ? 6 :
                          activeTab === 'positions' ? 5 :
                          activeTab === 'branches' ? 3 : 
                          isSelectionOption(activeTab) ? 3 : 2
                        } className="text-center py-8 text-slate-500">
                          {searchTerm ? 'Nenhum item encontrado' : 'Nenhum item cadastrado'}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  return filteredData.map((item) => (
                    <TableRow key={item.id} className="table-row">
                      {activeTab === 'companyAddresses' ? (
                        <>
                          <TableCell className="font-medium">{item.nome_empresa}</TableCell>
                          <TableCell>{item.endereco_completo}</TableCell>
                          <TableCell>{item.cidade}</TableCell>
                          <TableCell>{item.estado}</TableCell>
                          <TableCell className="text-center">
                            {item.is_default ? (
                              <Badge variant="default">Padrão</Badge>
                            ) : (
                              <Badge variant="outline">-</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDialog(activeTab, 'edit', item)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, type: activeTab, item })}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : activeTab === 'positions' ? (
                        <>
                          <TableCell className="font-medium">{item.cargo}</TableCell>
                          <TableCell>{item.empresa}</TableCell>
                          <TableCell>{item.departamento || '-'}</TableCell>
                          <TableCell>{item.modalidade || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openCargoDialog('edit', item)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, type: 'positions', item })}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          {activeTab === 'branches' && (
                            <TableCell>
                              {companies.find(c => c.id === item.company_id)?.name || 'N/A'}
                            </TableCell>
                          )}
                          {isSelectionOption(activeTab) && (
                            <TableCell className="text-right font-medium text-green-600">
                              {formatCurrency(item.value)}
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="flex items-center justify-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDialog(activeTab, 'edit', item)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, type: activeTab, item })}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog({ open: false, type: '', action: 'create', data: getDefaultDialogData() })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.action === 'edit' ? 'Editar' : 'Adicionar'} {getTabTitle(dialog.type).slice(0, -1)}
            </DialogTitle>
            <DialogDescription>
              {dialog.action === 'edit' 
                ? 'Edite as informações do item selecionado'
                : 'Preencha as informações para criar um novo item'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {dialog.type === 'companyAddresses' ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome_empresa">Empresa</Label>
                    <select
                      id="nome_empresa"
                      value={dialog.data.nome_empresa}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, nome_empresa: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione uma empresa</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.name}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cep">CEP</Label>
                    <div className="flex space-x-2">
                      <Input
                        id="cep"
                        value={dialog.data.cep}
                        onChange={(e) => setDialog(prev => ({
                          ...prev,
                          data: { ...prev.data, cep: e.target.value }
                        }))}
                        placeholder="00000-000"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => buscarCEP(dialog.data.cep)}
                        disabled={!dialog.data.cep || dialog.data.cep.replace(/\D/g, '').length !== 8}
                        title="Buscar CEP"
                      >
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="endereco_completo">Endereço Completo</Label>
                  <Input
                    id="endereco_completo"
                    value={dialog.data.endereco_completo}
                    onChange={(e) => setDialog(prev => ({
                      ...prev,
                      data: { ...prev.data, endereco_completo: e.target.value }
                    }))}
                    placeholder="Endereço completo para APIs de geolocalização"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="logradouro">Logradouro</Label>
                    <Input
                      id="logradouro"
                      value={dialog.data.logradouro}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, logradouro: e.target.value }
                      }))}
                      placeholder="Rua, Avenida, etc."
                    />
                  </div>
                  <div>
                    <Label htmlFor="numero">Número</Label>
                    <Input
                      id="numero"
                      value={dialog.data.numero}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, numero: e.target.value.replace(/\D/g, '') }
                      }))}
                      inputMode="numeric"
                      placeholder="123"
                    />
                  </div>
                  <div>
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={dialog.data.complemento}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, complemento: e.target.value }
                      }))}
                      placeholder="Sala, Andar (opcional)"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      value={dialog.data.bairro}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, bairro: e.target.value }
                      }))}
                      placeholder="Centro"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      value={dialog.data.cidade}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, cidade: e.target.value }
                      }))}
                      placeholder="São Paulo"
                    />
                  </div>
                  <div>
                    <Label htmlFor="estado">Estado</Label>
                    <Input
                      id="estado"
                      value={dialog.data.estado}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, estado: e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2) }
                      }))}
                      maxLength={2}
                      placeholder="SP"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={dialog.data.is_default}
                    onChange={(e) => setDialog(prev => ({
                      ...prev,
                      data: { ...prev.data, is_default: e.target.checked }
                    }))}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="is_default">Definir como endereço padrão</Label>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={dialog.data.name}
                    onChange={(e) => setDialog(prev => ({
                      ...prev,
                      data: { ...prev.data, name: e.target.value }
                    }))}
                    placeholder="Digite o nome"
                  />
                </div>
                {dialog.type === 'branches' && (
                  <div>
                    <Label htmlFor="company">Empresa</Label>
                    <select
                      id="company"
                      value={dialog.data.company_id}
                      onChange={(e) => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, company_id: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione uma empresa</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {isSelectionOption(dialog.type) && (
                  <div>
                    <Label htmlFor="value">Valor</Label>
                    <CurrencyInput
                      id="value"
                      value={dialog.data.value}
                      onChange={v => setDialog(prev => ({
                        ...prev,
                        data: { ...prev.data, value: v }
                      }))}
                      placeholder="0,00"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialog({ open: false, type: '', action: 'create', data: getDefaultDialogData() })}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              className="btn-primary"
              disabled={
                (dialog.type === 'companyAddresses' ? (
                  !dialog.data.nome_empresa || 
                  !dialog.data.endereco_completo || 
                  !dialog.data.cep ||
                  !dialog.data.logradouro ||
                  !dialog.data.numero ||
                  !dialog.data.bairro ||
                  !dialog.data.cidade ||
                  !dialog.data.estado
                ) : (
                  !dialog.data.name || 
                  (dialog.type === 'branches' && !dialog.data.company_id) ||
                  (isSelectionOption(dialog.type) && dialog.data.value < 0)
                ))
              }
            >
              {dialog.action === 'edit' ? 'Atualizar' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cargo + Salário Dialog (Cadastros > Cargos) */}
      <Dialog
        open={cargoDialog.open}
        onOpenChange={(open) => !open && setCargoDialog({ open: false, action: 'create', data: getDefaultCargoData() })}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {cargoDialog.action === 'edit' ? 'Editar Cargo' : 'Adicionar Cargo'}
            </DialogTitle>
            <DialogDescription>
              {cargoDialog.action === 'edit'
                ? 'Edite as informações do cargo e suas faixas salariais'
                : 'Preencha os dados do cargo e suas faixas salariais'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cargo_empresa">Empresa *</Label>
                <select
                  id="cargo_empresa"
                  value={cargoDialog.data.empresa}
                  onChange={(e) => setCargoDialog(prev => ({ ...prev, data: { ...prev.data, empresa: e.target.value } }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione uma empresa</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.name}>{company.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="cargo_nome">Cargo *</Label>
                <Input
                  id="cargo_nome"
                  value={cargoDialog.data.cargo}
                  onChange={(e) => setCargoDialog(prev => ({ ...prev, data: { ...prev.data, cargo: e.target.value } }))}
                  placeholder="Nome do cargo"
                />
              </div>
              <div>
                <Label htmlFor="cargo_departamento">Departamento</Label>
                <Input
                  id="cargo_departamento"
                  value={cargoDialog.data.departamento}
                  onChange={(e) => setCargoDialog(prev => ({ ...prev, data: { ...prev.data, departamento: e.target.value } }))}
                  placeholder="Departamento"
                />
              </div>
              <div>
                <Label htmlFor="cargo_modalidade">Modalidade</Label>
                <select
                  id="cargo_modalidade"
                  value={cargoDialog.data.modalidade}
                  onChange={(e) => setCargoDialog(prev => ({ ...prev, data: { ...prev.data, modalidade: e.target.value } }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="CLT">CLT</option>
                  <option value="PJ">PJ</option>
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-slate-700">Faixas Salariais</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {SALARY_FIELDS.map(field => (
                  <div key={field.key}>
                    <Label htmlFor={field.key}>{field.label}</Label>
                    <CurrencyInput
                      id={field.key}
                      value={cargoDialog.data[field.key]}
                      onChange={v => setCargoDialog(prev => ({ ...prev, data: { ...prev.data, [field.key]: v } }))}
                      placeholder="0,00"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCargoDialog({ open: false, action: 'create', data: getDefaultCargoData() })}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveCargo}
              className="btn-primary"
              disabled={!cargoDialog.data.empresa || !cargoDialog.data.cargo}
            >
              {cargoDialog.action === 'edit' ? 'Atualizar' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, type: '', item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover <strong>{deleteDialog.item?.name || deleteDialog.item?.cargo}</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, type: '', item: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteDialog.type, deleteDialog.item)}
              className="btn-danger"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigPage;